import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { parseLocalYMD, toLocalYMD } from "../../utils/fechasFuturosPanel";
import { esFechaReagendable, reagendarEntryFuturo } from "../../utils/reagendarEntryFuturo";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import { calcularRestriccionesFecha } from "../SelectorFechaDocumento";

// Celda de fecha del panel "Movimientos Futuros" (Fase 1 de "Futuros: reagendar", 2026-09-23): un
// click abre un calendario flotante (mismo patrón que el picker de cobro de VentasFacturas —
// `<DatePicker inline onClickOutside>` posicionado `fixed` según el `getBoundingClientRect` del
// click) y reagenda la fila SIN pasar por el kebab, para las ~11 categorías donde
// `esFechaReagendable` da true (ver src/utils/reagendarEntryFuturo.js).
//
// Para las filas NO reagendables, esta celda es un `<td>` puramente pasivo: mismo className/style,
// mismos `children` (la pill vencido/override/normal ya armada por FlujoCajaV2/index.js — no se toca
// esa lógica acá), sin cursor de click y SIN interceptar el evento — el click sigue burbujeando al
// `onClick` del `<tr>` exactamente como hoy (p.ej. abre PlanCuotasModal en una fila "facturado" con
// cuotas). Cero cambio visual/funcional para esas filas (10.244 — transformación de presentación
// declarada, nunca silenciosa).

// Exportado para tests (Fase 1d, Commit 4): reproduce EXACTO el segundo tramo de `handleChange` — la
// escritura en DB ya está confirmada arriba, esto solo aplica el patch (`onSaved`) y decide el toast.
// Separado a una función standalone para poder probarlo SIN renderizar React: el proyecto no tiene
// `@testing-library/react` instalado (agregar una dependencia nueva de testing implica un `npm
// install` en la carpeta principal — fuera de alcance de esta fase, y ningún otro test del proyecto
// renderiza componentes) — así el contrato real "nunca dos toasts para el mismo click" queda cubierto
// por un test genuino sobre el código de producción, no por una copia paralela de la lógica.
// `toastApi` default = `toast` real (producción); los tests inyectan un mock.
export const aplicarPatchYToast = async (resultado, onSaved, toastApi = toast) => {
  try {
    // `onSaved` (hoy `aplicarPatchReagendado`) devuelve una PROMESA que resuelve solo cuando el patch
    // quedó efectivamente aplicado (los `setX` correspondientes ya se llamaron) — recién ahí se
    // muestra el toast de éxito (Fase 1c, Commit 4a, preservado acá).
    await onSaved?.(resultado);
    toastApi.success("Fecha actualizada");
  } catch (err) {
    // `err.toastYaMostrado` (Fase 1d): algunos rechazos (p.ej. el refetch de sueldo fallido) ya
    // avisaron con SU PROPIO toast, más específico, dentro de `aplicarPatchReagendado` — acá no se
    // duplica. Para cualquier otro rechazo (p.ej. `_patchType` no reconocido) este es el ÚNICO toast:
    // la escritura ya está confirmada, solo la pantalla quedó desactualizada. Por construcción (este
    // catch corre SOLO si `onSaved` rechazó) el success de arriba nunca se disparó para este click —
    // success y error son mutuamente excluyentes, nunca ambos.
    if (!err?.toastYaMostrado) {
      toastApi.error("La fecha se guardó pero la fila no se actualizó en pantalla — recargá el panel");
    }
  }
};

const CeldaFechaReagendable = ({ f, ctx, className, style, onSaved, children }) => {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const reagendable = esFechaReagendable(f, ctx);

  const handleClickCelda = (e) => {
    if (!reagendable) return; // deja burbujear al onClick del <tr> — comportamiento sin cambios.
    e.stopPropagation();
    if (guardando) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ top: rect.bottom, left: rect.left });
    setAbierto(true);
  };

  const handleChange = async (date) => {
    if (!date) return;
    setAbierto(false);
    setGuardando(true);
    // Fase 1d (cierra 2 de las fisuras menores de la auditoría de Fase 1c): DOS pasos separados, no
    // un solo try/catch. El primero es la escritura real en DB (reagendarEntryFuturo) — si falla,
    // NADA se guardó, el mensaje es "Error al reagendar" y no hay ambigüedad. El segundo
    // (`aplicarPatchYToast`, arriba) aplica el patch al estado local y decide el toast — para cuando
    // llega acá, la escritura en DB YA SE CONFIRMÓ; si falla, lo único que falló es el REFRESCO
    // VISUAL, nunca el guardado. Antes ambos pasos compartían un catch y mostraban el mismo "Error al
    // reagendar" — mensaje engañoso para una fila que en realidad SÍ se guardó.
    let resultado;
    try {
      const nuevaFecha = toLocalYMD(date);
      resultado = await reagendarEntryFuturo(f, nuevaFecha, ctx);
    } catch (err) {
      toast.error(`Error al reagendar: ${err.message || err}`);
      setGuardando(false);
      return;
    }
    await aplicarPatchYToast(resultado, onSaved);
    setGuardando(false);
  };

  // Precarga con la fecha comprometida REAL (fecha_original si está vencida-colapsada — 10.244 —, si
  // no fecha_display), nunca con hoy. Piso de hoy inclusive (decisión de producto): minDate + filterDate
  // como doble guard de UI (el guard real, no confiable-solo-en-UI, vive en reagendarEntryFuturo).
  // Commit 2b (2026-09-26): minDate/filterDate salen de `calcularRestriccionesFecha`
  // (SelectorFechaDocumento, 10.1) — MISMA fuente que usarán todos los modales migrados de la §10, en
  // vez de calcularlos acá con su propia copia de hoyDelPais/esDiaHabil. Modo ESTRICTO siempre (esta
  // celda es el click-para-reagendar, nunca alta de dato nuevo). Cero cambio visual ni de
  // comportamiento — el JSX/inline del DatePicker de abajo sigue exactamente igual.
  const fechaPrecarga = parseLocalYMD(f?.fecha_original || f?.fecha_display);
  const { minDate: hoyMedianoche, filterDate } = calcularRestriccionesFecha({
    modo: MODOS_VALIDACION.ESTRICTO,
    pais: ctx?.pais,
  });

  return (
    <td
      className={className}
      style={{ ...(style || {}), cursor: reagendable ? "pointer" : undefined }}
      onClick={handleClickCelda}
    >
      {children}
      {abierto && pos && (
        <div
          style={{
            position: "fixed",
            top: pos.top,
            left: Math.min(pos.left, window.innerWidth - 280),
            zIndex: 9999,
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            borderRadius: 8,
            background: "#fff",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DatePicker
            selected={fechaPrecarga}
            onChange={handleChange}
            minDate={hoyMedianoche}
            // Piso de hoy Y bloqueo de fin de semana (Fase 1c, Commit 3) — mismo criterio que usa el
            // validador real de `reagendarEntryFuturo` (doble guard, nunca confiar solo en la UI), ahora
            // vía `calcularRestriccionesFecha` (Commit 2b, 10.1).
            filterDate={filterDate}
            onClickOutside={() => setAbierto(false)}
            disabled={guardando}
            inline
          />
        </div>
      )}
    </td>
  );
};

export default CeldaFechaReagendable;
